/** The dot that says this is happening now.
 *
 * The same mark serves three places — the brand in the header, the LIVE
 * kicker on a spectate page, a streaming user in the admin list — so the glow
 * and the pulse are separable: the brand glows without pulsing, a live row
 * pulses without needing the glow. Under `prefers-reduced-motion` the
 * stylesheet flattens the animation and the dot simply stays lit, which still
 * reads as "on".
 */

import type { CSSProperties } from 'react';

export interface PulseDotProps {
  size?: number;
  /** `accent` for live, `error` for recording, `ok`/`warn` for a state dot. */
  tone?: 'accent' | 'ok' | 'warn' | 'error' | 'neutral';
  /** The 8px bloom the design gives the brand mark and the live indicator. */
  glow?: boolean;
  /** Off for a dot that is a legend key rather than a live signal. */
  pulse?: boolean;
  className?: string;
  style?: CSSProperties;
}

const TONE: Record<NonNullable<PulseDotProps['tone']>, string> = {
  accent: 'var(--color-accent)',
  ok: 'var(--color-ok)',
  warn: 'var(--color-warn)',
  error: 'var(--color-error)',
  neutral: 'var(--color-neutral-500)',
};

export function PulseDot({
  size = 7,
  tone = 'accent',
  glow = false,
  pulse = true,
  className,
  style,
}: PulseDotProps) {
  const colour = TONE[tone];
  return (
    <span
      aria-hidden="true"
      className={className}
      style={{
        width: size,
        height: size,
        flex: 'none',
        borderRadius: '50%',
        background: colour,
        boxShadow: glow ? `0 0 8px ${colour}` : undefined,
        animation: pulse ? 'livepulse 1.6s infinite' : undefined,
        ...style,
      }}
    />
  );
}

export default PulseDot;
